import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ACADEMY_CATALOG, AcademySlugImage } from "../components/icons/academyMeta";
import { QtChevronRight, QtClose, QtIcon, QtMenu } from "../components/icons/QtIcon";
import {
  CAREERS_MEDIA,
  LANDING_FEATURES,
  LANDING_GALLERY,
  LANDING_STEPS,
} from "./careersMedia";
import "./landing.scss";
import {
  getCandidateDisplayName,
  isCandidateLoggedIn,
  signOutCandidate,
} from "../utils/candidateAuth";
import { getCareersPublicStats } from "../services/academyApi";
import { listJobs } from "../services/jobsApi";
import { EmploymentPill } from "./jobs/jobsShared";
import CareersFooter from "../components/CareersFooter";

function formatRegisteredCount(count) {
  if (count == null) return "-";
  return new Intl.NumberFormat("en-ZA").format(count);
}

const FORM_URL = "https://forms.gle/99hRPmPXtZrLbTRW7";

const FAQ = [
  {
    q: "Is QikTruck training free?",
    a: "Yes. All program modules are free once you sign in with your email. You can learn on your phone or computer.",
  },
  {
    q: "Do I need experience in logistics?",
    a: "No. Whether you have matric, a certificate, or years on the road, there is a program path for you.",
  },
  {
    q: "How do I apply for jobs?",
    a: "Open the Open positions page, pick a role, sign in with your email and apply with a short intro video. You can also start with a program to build your profile.",
  },
  {
    q: "What roles are available?",
    a: "Drivers, fleet coordinators, warehouse staff, dispatchers, and office roles across South Africa's logistics industry.",
  },
];

export default function LandingPage() {
  const [navOpen, setNavOpen] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [displayName, setDisplayName] = useState(null);
  const [registeredCandidates, setRegisteredCandidates] = useState(null);
  const [openJobs, setOpenJobs] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoggedIn(isCandidateLoggedIn());
    setDisplayName(getCandidateDisplayName());
  }, []);

  useEffect(() => {
    let cancelled = false;
    listJobs()
      .then((data) => {
        if (!cancelled) setOpenJobs((data.jobs || []).slice(0, 3));
      })
      .catch(() => {
        if (!cancelled) setOpenJobs([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getCareersPublicStats()
      .then((data) => {
        if (!cancelled) setRegisteredCandidates(data.registered_candidates ?? null);
      })
      .catch(() => {
        if (!cancelled) setRegisteredCandidates(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const closeNav = () => setNavOpen(false);

  const handleSignOut = () => {
    closeNav();
    signOutCandidate({ redirectTo: "/" });
  };

  return (
    <div className="careers-landing">
      <Helmet>
        <title>QikTruck Careers - Logistics jobs &amp; free training programs</title>
        <meta
          name="description"
          content="Build your logistics career in South Africa. Free training programs with modules, lessons, and quizzes, plus a path from learning to placement."
        />
        <link rel="canonical" href="https://careers.qiktruck.co.za/" />
      </Helmet>

      <div className="careers-landing__bg" aria-hidden="true" />

      <header className="careers-nav">
        <div className="careers-nav__inner">
          <Link to="/" className="careers-nav__brand" onClick={closeNav}>
            QikTruck <span>Careers</span>
            <span className="careers-nav__new">New portal</span>
          </Link>

          <button
            type="button"
            className="careers-nav__toggle"
            aria-label={navOpen ? "Close menu" : "Open menu"}
            aria-expanded={navOpen}
            onClick={() => setNavOpen((o) => !o)}
          >
            {navOpen ? <QtClose /> : <QtMenu />}
          </button>

          <nav className={`careers-nav__links${navOpen ? " careers-nav__links--open" : ""}`}>
            <Link to="/jobs" className="careers-nav__link" onClick={closeNav}>
              Open positions
            </Link>
            <a href="#programs" className="careers-nav__link" onClick={closeNav}>
              Programs
            </a>
            <a href="#how-it-works" className="careers-nav__link" onClick={closeNav}>
              How it works
            </a>
            <Link to="/creator" className="careers-nav__link" onClick={closeNav}>
              Creator Program
            </Link>
            <a href="#faq" className="careers-nav__link" onClick={closeNav}>
              FAQ
            </a>
            <Link to="/academy" className="careers-nav__link" onClick={closeNav}>
              Training
            </Link>
            {loggedIn ? (
              <>
                {displayName && (
                  <p className="careers-nav__signed-in">
                    Signed in as <strong>{displayName}</strong>
                  </p>
                )}
                <Link to="/academy" className="careers-btn careers-btn--primary" onClick={closeNav}>
                  Continue training
                </Link>
                <Link to="/me" className="careers-nav__link" onClick={closeNav}>
                  My progress
                </Link>
                <button type="button" className="careers-btn careers-btn--ghost careers-nav__signout" onClick={handleSignOut}>
                  Sign out
                </button>
              </>
            ) : (
              <Link to="/login" className="careers-btn careers-btn--primary" onClick={closeNav}>
                Sign in
              </Link>
            )}
          </nav>
        </div>
      </header>

      <section className="careers-section careers-hero">
        <div className="careers-hero__copy">
          <div className="careers-hero__badges">
            <span className="careers-badge careers-badge--new">New portal</span>
            <p className="careers-eyebrow">South Africa logistics careers</p>
          </div>
          <h1 className="careers-h1">
            Learn logistics skills.
            <br />
            Land the right job.
          </h1>
          <p className="careers-lead">
            QikTruck Careers combines free training programs with a real hiring pipeline, so you
            can prove your skills before you apply.
          </p>
          <div className="careers-hero__actions">
            {loggedIn ? (
              <>
                <Link to="/academy" className="careers-btn careers-btn--primary">
                  Continue training
                </Link>
                <Link to="/me" className="careers-btn careers-btn--secondary">
                  My progress
                </Link>
              </>
            ) : (
              <>
                <Link to="/login" className="careers-btn careers-btn--primary">
                  Get started - it&apos;s free
                </Link>
                <Link to="/academy" className="careers-btn careers-btn--secondary">
                  Browse programs
                </Link>
              </>
            )}
          </div>
          <div className="careers-hero__stats">
            <div className="careers-stat">
              <strong>{formatRegisteredCount(registeredCandidates)}</strong>
              <span>Registered candidates</span>
            </div>
            <div className="careers-stat">
              <strong>5</strong>
              <span>Career programs</span>
            </div>
            <div className="careers-stat">
              <strong>Free</strong>
              <span>Training modules</span>
            </div>
            <div className="careers-stat">
              <strong>SA</strong>
              <span>Nationwide opportunities</span>
            </div>
          </div>
        </div>

        <div className="careers-hero__visual">
          <div className="careers-hero-photo">
            <img
              src={CAREERS_MEDIA.heroMain}
              alt="South African professional building a logistics career through QikTruck training programs"
              loading="eager"
              fetchPriority="high"
              width={1400}
              height={933}
              style={{ objectPosition: "center 25%" }}
            />
            <div className="careers-hero-photo__badge">
              <span className="careers-hero-photo__badge-icon" aria-hidden="true">
                <QtIcon name="book-open" size={22} />
              </span>
              <div>
                <strong>QikTruck Programs</strong>
                <span>Free training · Any device</span>
              </div>
            </div>
          </div>

          <div className="careers-academy-preview">
            <p className="careers-academy-preview__label">Start here</p>
            {ACADEMY_CATALOG.slice(0, 3).map((a) => (
              <Link key={a.slug} to={`/academy/${a.slug}`} className="careers-academy-item">
                <div className="careers-academy-item__thumb">
                  <AcademySlugImage slug={a.slug} className="careers-academy-item__photo" />
                </div>
                <div>
                  <p className="careers-academy-item__title">{a.title}</p>
                  <p className="careers-academy-item__meta">{a.kicker}</p>
                </div>
              </Link>
            ))}
            <Link to="/academy" className="careers-btn careers-btn--ghost careers-academy-preview__cta careers-btn--with-icon">
              <span>View all programs</span>
              <QtChevronRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      <section className="careers-gallery-band" aria-label="Logistics careers in South Africa">
        <div className="careers-gallery-band__inner">
          {LANDING_GALLERY.map((item) => (
            <figure key={item.src} className="careers-gallery-item">
              <img
                src={item.src}
                alt={item.alt}
                loading="lazy"
                width={900}
                height={675}
                style={{ objectPosition: item.objectPosition }}
              />
              <figcaption>{item.label}</figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section id="open-positions" className="careers-section">
        <div className="careers-section-head">
          <p className="careers-eyebrow">Open positions</p>
          <h2 className="careers-h2">Work with QikTruck</h2>
          <p className="careers-lead" style={{ margin: "0 auto" }}>
            Sales, logistics admin, part time and commission only roles. Apply with a short intro
            video.
          </p>
        </div>
        {openJobs.length > 0 ? (
          <div className="jobs-list">
            {openJobs.map((job) => (
              <Link key={job.id} to={`/jobs/${job.slug}`} className="job-card">
                <div className="job-card__top">
                  <h3 className="job-card__title">{job.title}</h3>
                  <div className="job-card__pills">
                    <EmploymentPill type={job.employment_type} />
                  </div>
                </div>
                {job.summary ? <p className="job-card__summary">{job.summary}</p> : null}
                <div className="job-card__meta">
                  {job.pay_summary ? <span>{job.pay_summary}</span> : null}
                  {job.location ? <span>{job.location}</span> : null}
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <p className="careers-lead" style={{ margin: "0 auto", textAlign: "center" }}>
            No open roles right now. Finish a training program so you are ready when one opens.
          </p>
        )}
        <div style={{ textAlign: "center", marginTop: 24 }}>
          <Link to="/jobs" className="careers-btn careers-btn--secondary">
            See all open positions
          </Link>
        </div>
      </section>

      <section id="programs" className="careers-section careers-section--muted">
        <div className="careers-section__inner">
          <div className="careers-section-head">
            <p className="careers-eyebrow">Training programs</p>
            <h2 className="careers-h2">Five programs. One career platform.</h2>
            <p className="careers-lead" style={{ margin: "0 auto" }}>
              Each program is built around real competencies, from the cab to the control room.
            </p>
          </div>
          <div className="careers-grid-5">
            {ACADEMY_CATALOG.map((a) => (
              <Link key={a.slug} to={`/academy/${a.slug}`} className={`careers-academy-tile careers-academy-tile--${a.slug}`}>
                <div className="careers-academy-tile__media">
                  <AcademySlugImage slug={a.slug} className="careers-academy-tile__photo" />
                </div>
                <div className="careers-academy-tile__body">
                  <span className="careers-eyebrow" style={{ marginBottom: 0, fontSize: 11 }}>
                    {a.kicker}
                  </span>
                  <h3>{a.title}</h3>
                  <p>{a.purpose}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section id="how-it-works" className="careers-section">
        <div className="careers-section-head">
          <p className="careers-eyebrow">How it works</p>
          <h2 className="careers-h2">From sign-in to placement</h2>
        </div>
        <div className="careers-steps-visual">
          {LANDING_STEPS.map((s, i) => (
            <article
              key={s.title}
              className={`careers-step-visual ${i % 2 === 1 ? "careers-step-visual--reverse" : ""}`}
            >
              <div className="careers-step-visual__media">
                <img
                  src={s.image}
                  alt={s.alt}
                  loading="lazy"
                  width={960}
                  height={600}
                  style={{ objectPosition: s.objectPosition }}
                />
                <span className="careers-step-visual__num">{i + 1}</span>
              </div>
              <div className="careers-step-visual__body">
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="careers-section careers-section--muted">
        <div className="careers-section__inner">
          <div className="careers-split-banner">
            <div className="careers-split-banner__media">
              <img
                src={CAREERS_MEDIA.bannerTeam}
                alt="Diverse South African logistics career candidates in QikTruck training programs"
                loading="lazy"
                width={1200}
                height={800}
                style={{ objectPosition: "center 30%" }}
              />
            </div>
            <div className="careers-split-banner__body">
              <p className="careers-eyebrow">Built for South Africa</p>
              <h2 className="careers-h2">Real logistics. Real careers.</h2>
              <p className="careers-lead" style={{ marginBottom: 0 }}>
                From drivers and dispatchers to warehouse teams and fleet coordinators - QikTruck
                Careers helps you learn the skills employers need, then connects you to
                opportunities nationwide.
              </p>
            </div>
          </div>

          <div className="careers-section-head" style={{ marginTop: 48 }}>
            <p className="careers-eyebrow">Why QikTruck</p>
            <h2 className="careers-h2">Built for people entering logistics</h2>
          </div>
          <div className="careers-grid-3">
            {LANDING_FEATURES.map((f) => (
              <article key={f.title} className="careers-card careers-card--visual">
                <div className="careers-card__photo">
                  <img
                    src={f.image}
                    alt={f.alt}
                    loading="lazy"
                    width={720}
                    height={405}
                    style={{ objectPosition: f.objectPosition }}
                  />
                </div>
                <h3>{f.title}</h3>
                <p>{f.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="faq" className="careers-section">
        <div className="careers-section-head">
          <p className="careers-eyebrow">FAQ</p>
          <h2 className="careers-h2">Common questions</h2>
        </div>
        <div className="careers-faq">
          {FAQ.map((item) => (
            <details key={item.q}>
              <summary>{item.q}</summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="careers-section careers-section--tight">
        <div
          className="careers-cta careers-cta--photo"
          style={{ backgroundImage: `url(${CAREERS_MEDIA.ctaBackground})` }}
        >
          <div className="careers-cta__overlay">
            <h2>Ready to start?</h2>
            <p>
              Sign in, complete your first module, and show employers you&apos;re serious about
              logistics.
            </p>
            <div className="careers-cta__actions">
              <Link to="/login" className="careers-btn careers-btn--secondary">
                Sign in with email
              </Link>
              <a
                href={FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="careers-btn careers-btn--ghost"
              >
                Quick application form
              </a>
            </div>
          </div>
        </div>
      </section>

      <CareersFooter />
    </div>
  );
}
